if (!customElements.get("product-recommendations")) {
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      // Wait for section HTML to finish parsing, including editor replacements.
      this.frame = requestAnimationFrame(() => {
        this.wrapper = this.querySelector(".swiper-wrapper");
        this.loaded = false;
        this.style.display = "block";
        this.observe();
      });
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      this.observer?.disconnect();
      this.carousel?.destroy(true, true);
    }

    observe() {
      if (!this.dataset.lazyLoad || !("IntersectionObserver" in window)) {
        this.load();
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            this.observer.disconnect();
            this.load();
          });
        },
        { rootMargin: "700px 0px" },
      );
      this.observer.observe(this);
    }

    load() {
      if (this.loaded) return;
      this.loaded = true;
      this.dataset.state = "loading";

      if (this.dataset.source === "collection") {
        this.finalize();
        return;
      }

      if (this.dataset.source === "recent") {
        this.getRecentlyViewed();
        return;
      }

      if (this.dataset.source === "similiar" || this.dataset.source === "category") {
        this.getProductListView(this.dataset.source);
        return;
      }

      this.getRecommendations();
    }

    getRecommendations() {
      if (!this.dataset.productId) return this.finalize();
      fetch(
        `${window.Shopify.routes.root}recommendations/products?product_id=${this.dataset.productId}&limit=8&section_id=product-recommendations-content&intent=${this.dataset.source}`,
      )
        .then((response) => response.text())
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, "text/html");
          const cards = doc.querySelector(".swiper-wrapper");
          if (cards?.querySelector("product-card")) {
            this.wrapper.innerHTML = cards.innerHTML;
          }
          this.finalize();
        })
        .catch(() => this.finalize());
    }

    getProductListView(source) {
      if (!this.dataset.productHandle) return this.finalize();
      fetch(`/products/${this.dataset.productHandle}?view=recommendations-${source}`)
        .then((response) => response.text())
        .then((html) => {
          if (html.trim()) {
            this.wrapper.innerHTML = html;
          }
          this.finalize();
        })
        .catch(() => this.finalize());
    }

    getRecentlyViewed() {
      const storageKey = "pdp_recently_viewed";
      let storedRecent = [];

      try {
        const parsedRecent = JSON.parse(localStorage.getItem(storageKey) || "[]");
        storedRecent = Array.isArray(parsedRecent) ? parsedRecent : [];
      } catch {
        storedRecent = [];
      }

      const recent = storedRecent.filter((handle) => handle !== "surplus_variants");
      const requests = [];

      if (JSON.stringify(recent) !== localStorage.getItem(storageKey)) {
        localStorage.setItem(storageKey, JSON.stringify(recent));
      }

      if (
        recent.length < 1 ||
        (recent.length === 1 && recent[0] === this.dataset.productHandle)
      ) {
        this.finalize();
        return;
      }

      recent.forEach((handle) => {
        if (handle !== this.dataset.productHandle) {
          requests.push(`/products/${handle}?view=card`);
        }
      });

      Promise.all(requests.map((url) => fetch(url).then((response) => response.text())))
        .then((responses) => {
          responses.forEach((html) => {
            const doc = new DOMParser().parseFromString(html, "text/html");
            if (doc.querySelectorAll("product-card").length === 1) {
              this.wrapper.innerHTML += `<div class="swiper-slide">${html}</div>`;
            }
          });
          this.finalize();
        })
        .catch(() => this.finalize());
    }

    finalize() {
      if (!this.isConnected) return;
      if (this.wrapper?.querySelector("product-card")) {
        this.dataset.state = "success";
        this.initCarousel();
        return;
      }

      this.dataset.state = "error";
      if (this.closest(".product__complementary-carousel")) {
        this.closest(".product__complementary-carousel").remove();
      } else {
        this.remove();
      }
    }

    initCarousel() {
      const scrollbar = this.querySelector(".swiper-scrollbar");
      const prev = this.querySelector(".arrow--left");
      const next = this.querySelector(".arrow--right");
      const isStandalone = Boolean(this.closest(".section--recommendations"));
      const configuredSpaceBetween = Number(this.dataset.spaceBetween);
      const hasConfiguredSpaceBetween = Number.isFinite(configuredSpaceBetween);
      this.carousel = new Swiper(this.querySelector(".swiper-container"), {
        loop: false,
        grabCursor: false,
        speed: 500,
        spaceBetween: isStandalone ? 4 : 8,
        slidesPerView: isStandalone ? "auto" : 2,
        watchSlidesProgress: true,
        scrollbar: {
          el: scrollbar,
          enabled: true,
          dragSize: isStandalone ? 65 : "auto",
        },
        navigation: { nextEl: next, prevEl: prev },
        breakpoints: {
          768: {
            spaceBetween: hasConfiguredSpaceBetween ? configuredSpaceBetween : 16,
            slidesPerView: 3,
          },
          992: {
            spaceBetween: hasConfiguredSpaceBetween ? configuredSpaceBetween : 16,
            slidesPerView: isStandalone ? 3 : this.dataset.slidesPerView,
          },
          1200: {
            spaceBetween: hasConfiguredSpaceBetween ? configuredSpaceBetween : 16,
            slidesPerView: isStandalone ? "auto" : this.dataset.slidesPerView,
          },
          1900: {
            spaceBetween: hasConfiguredSpaceBetween ? configuredSpaceBetween : 16,
            slidesPerView: isStandalone ? "auto" : this.dataset.slidesPerView,
          },
        },
      });
      this.style.display = "block";
      setTimeout(() => {
        window.dispatchEvent(new Event("resize"));
      }, 100);
    }
  }
  customElements.define("product-recommendations", ProductRecommendations);
}
